/**
 * AI Prompt Management Service
 * 
 * Handles API calls for managing versioned AI prompts for school summaries.
 * Admin-only service for iterative prompt improvement.
 */

import { auth } from '../firebase';
import config from '../config';

/**
 * List all prompt versions grouped by type
 * @returns {Promise<Object>} Prompts organized by type (geographic, competitive, projections)
 */
export const listPrompts = async () => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }
    const token = await user.getIdToken();
    
    const response = await fetch(
      `${config.apiUrl}/v2/admin/ai/prompts`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to list prompts');
    }
    
    return await response.json();
  } catch (error) {
    console.error('AI Prompt Service Error (list):', error);
    throw error;
  }
};

/**
 * Get the active prompt version for a specific type
 * @param {string} promptType - 'geographic', 'competitive', or 'projections'
 * @returns {Promise<Object>} Active prompt version
 */
export const getActivePrompt = async (promptType) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }
    const token = await user.getIdToken();
    
    const response = await fetch(
      `${config.apiUrl}/v2/admin/ai/prompts/${promptType}/active`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get active prompt');
    }
    
    return await response.json();
  } catch (error) {
    console.error('AI Prompt Service Error (get active):', error);
    throw error;
  }
};

/**
 * Create a new prompt version (automatically becomes active)
 * @param {string} promptType - 'geographic', 'competitive', or 'projections'
 * @param {string} systemPrompt - The system/developer prompt
 * @param {string} userPromptTemplate - The user prompt template with placeholders
 * @param {string} exampleOutput - Optional example output for few-shot learning
 * @param {string} notes - Optional notes about what changed
 * @returns {Promise<Object>} Created prompt version
 */
export const createPrompt = async (promptType, systemPrompt, userPromptTemplate, exampleOutput = '', notes = '') => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }
    const token = await user.getIdToken();
    
    const response = await fetch(
      `${config.apiUrl}/v2/admin/ai/prompts`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          prompt_type: promptType,
          system_prompt: systemPrompt,
          user_prompt_template: userPromptTemplate,
          example_output: exampleOutput,
          notes
        })
      }
    );
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create prompt');
    }
    
    return await response.json();
  } catch (error) {
    console.error('AI Prompt Service Error (create):', error);
    throw error;
  }
};

/**
 * Activate a specific prompt version
 * @param {string} promptId - UUID of the prompt version to activate
 * @returns {Promise<Object>} Activated prompt version
 */
export const activatePrompt = async (promptId) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }
    const token = await user.getIdToken();
    
    const response = await fetch(
      `${config.apiUrl}/v2/admin/ai/prompts/${promptId}/activate`,
      {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to activate prompt');
    }
    
    return await response.json();
  } catch (error) {
    console.error('AI Prompt Service Error (activate):', error);
    throw error;
  }
};

/**
 * Get a specific prompt version by ID
 * @param {string} promptId - UUID of the prompt version
 * @returns {Promise<Object>} Prompt version details
 */
export const getPrompt = async (promptId) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }
    const token = await user.getIdToken();
    
    const response = await fetch(
      `${config.apiUrl}/v2/admin/ai/prompts/${promptId}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get prompt');
    }
    
    return await response.json();
  } catch (error) {
    console.error('AI Prompt Service Error (get):', error);
    throw error;
  }
};

